import { ApiClient } from './apiClient';
import { getIntegrationsUrl } from '@shared';
import type { Redtail, Wealthbox, Salesforce, Prospecting } from './types';

export const IntegrationsClient = new ApiClient({
  baseURL: getIntegrationsUrl(),
});

// Query key factory for consistent key patterns across resources
export const queryKeys = {
  redtail: {
    all: ['redtail'] as const,
    resource: (resource: string) => [...queryKeys.redtail.all, resource] as const,
    lists: (resource: string) => [...queryKeys.redtail.resource(resource), 'list'] as const,
    list: (resource: string, params?: Record<string, unknown>) =>
      [...queryKeys.redtail.lists(resource), params] as const,
    details: (resource: string) => [...queryKeys.redtail.resource(resource), 'detail'] as const,
    detail: (resource: string, id: string) => [...queryKeys.redtail.details(resource), id] as const,
  },
  wealthbox: {
    all: ['wealthbox'] as const,
    resource: (resource: string) => [...queryKeys.wealthbox.all, resource] as const,
    lists: (resource: string) => [...queryKeys.wealthbox.resource(resource), 'list'] as const,
    list: (resource: string, params?: Record<string, unknown>) =>
      [...queryKeys.wealthbox.lists(resource), params] as const,
  },
  salesforce: {
    all: ['salesforce'] as const,
    resource: (resource: string) => [...queryKeys.salesforce.all, resource] as const,
    lists: (resource: string) => [...queryKeys.salesforce.resource(resource), 'list'] as const,
    list: (resource: string, params?: Record<string, unknown>) =>
      [...queryKeys.salesforce.lists(resource), params] as const,
  },
  prospecting: {
    all: ['prospecting'] as const,
    schema: (provider: string, objectName: string) =>
      [...queryKeys.prospecting.all, provider, 'schema', objectName] as const,
    sync: (provider: string, objectName: string) =>
      [...queryKeys.prospecting.all, provider, 'sync', objectName] as const,
    syncSettings: (provider: string, objectName: string) =>
      [...queryKeys.prospecting.all, provider, 'sync-settings', objectName] as const,
    duplicates: () => [...queryKeys.prospecting.all, 'duplicates'] as const,
    providers: () => [...queryKeys.prospecting.all, 'providers'] as const,
    prospects: (provider: string, query?: Record<string, unknown>) =>
      [...queryKeys.prospecting.all, provider, 'prospects', query] as const,
    prospect: (provider: string, prospectId: string) =>
      [...queryKeys.prospecting.all, provider, 'prospect', prospectId] as const,
    scoringSettings: (provider: string) => [...queryKeys.prospecting.all, provider, 'scoring-settings'] as const,
  },
};

export const wealthboxApi = {
  oauth: {
    getAuthorizeUrl: () =>
      IntegrationsClient.get<{ url: string }>('/wealthbox/oauth/authorize'),
    exchangeCode: (data: { code: string }) =>
      IntegrationsClient.post<Wealthbox.User>('/wealthbox/oauth/token', data),
  },
  auth: {
    status: () => IntegrationsClient.get<Wealthbox.User | null>('/wealthbox/status'),
    deactivate: () => IntegrationsClient.post<Wealthbox.User>('/wealthbox/user/deactivate'),
  },
  contacts: {
    list: (params?: Wealthbox.ContactListParams) =>
      IntegrationsClient.get<Wealthbox.ContactListResponse>('/wealthbox/contacts', params),
    get: (contactId: string) =>
      IntegrationsClient.get<Wealthbox.Contact>(`/wealthbox/contacts/${contactId}`),
    link: ({ contactId, clientId }: Wealthbox.LinkContactRequest) =>
      IntegrationsClient.post<Wealthbox.LinkedContact>(`/wealthbox/contacts/${contactId}/link`, {
        clientId,
      }),
    unlink: (contactId: string) =>
      IntegrationsClient.delete<{ success: boolean }>(`/wealthbox/contacts/${contactId}/unlink`),
  },
};

export const salesforceApi = {
  oauth: {
    getAuthorizeUrl: () =>
      IntegrationsClient.get<{ url: string; codeVerifier: string }>('/salesforce/oauth/authorize'),
    exchangeCode: (data: { code: string; codeVerifier?: string }) =>
      IntegrationsClient.post<Salesforce.User>('/salesforce/oauth/token', data),
  },
  auth: {
    status: () => IntegrationsClient.get<Salesforce.User | null>('/salesforce/status'),
    deactivate: () => IntegrationsClient.post<Salesforce.User>('/salesforce/user/deactivate'),
  },
  contacts: {
    list: (params?: Salesforce.ContactListParams) =>
      IntegrationsClient.get<Salesforce.ContactListResponse>('/salesforce/contacts', params),
    get: (contactId: string) =>
      IntegrationsClient.get<Salesforce.Contact>(`/salesforce/contacts/${contactId}`),
    link: ({ contactId, clientId }: Salesforce.LinkContactRequest) =>
      IntegrationsClient.post<Salesforce.LinkedContact>(`/salesforce/contacts/${contactId}/link`, {
        clientId,
      }),
    unlink: (contactId: string) =>
      IntegrationsClient.delete<{ success: boolean }>(`/salesforce/contacts/${contactId}/unlink`),
  },
};

export const redtailApi = {
  auth: {
    login: (data: Redtail.AuthRequest) =>
      IntegrationsClient.post<Redtail.User>('/redtail/authenticate', data),

    status: () => IntegrationsClient.get<Redtail.User | null>('/redtail/status'),

    deactivate: () => IntegrationsClient.post<Redtail.User>('/redtail/user/deactivate'),
  },

  contacts: {
    list: (params?: Redtail.ContactListParams) =>
      IntegrationsClient.get<Redtail.ContactListResponse>('/redtail/contacts', params),

    get: (contactId: string) =>
      IntegrationsClient.get<Redtail.Contact>(`/redtail/contacts/${contactId}`),

    update: (contactId: string, data: Partial<Redtail.Contact>) =>
      IntegrationsClient.post<{ contact: Redtail.Contact }>(`/redtail/contacts/${contactId}`, data),

    import: (data: Redtail.ImportContactsRequest) =>
      IntegrationsClient.post<string[]>('/redtail/contacts/import', data),

    link: ({ contactId, clientId }: Redtail.LinkContactRequest) =>
      IntegrationsClient.post<Redtail.LinkedContact>(`/redtail/contacts/${contactId}/link`, {
        clientId,
      }),

    unlink: (contactId: string) =>
      IntegrationsClient.delete<{ success: boolean }>(`/redtail/contacts/${contactId}/unlink`),
  },
};

/**
 * CRM prospecting, for whichever CRM the caller names. One client for every provider — adding a
 * CRM adds a value to `Prospecting.Provider`, not a new API surface.
 */
export const prospectingApi = {
  providers: {
    /** Which CRMs prospecting covers for this advisor, and which is active, paused or not scored yet. */
    list: () => IntegrationsClient.get<Prospecting.ProvidersResponse>('/prospecting/providers'),
  },
  schema: {
    discover: (provider: Prospecting.Provider, objectName: string) =>
      IntegrationsClient.get<Prospecting.SchemaDiscoveryResponse>(
        `/prospecting/${provider}/schema/${objectName}`,
      ),
    confirmFieldMap: (
      provider: Prospecting.Provider,
      objectName: string,
      decisions: Prospecting.FieldMapDecision[],
    ) =>
      IntegrationsClient.put<Prospecting.ConfirmFieldMapResponse>(
        `/prospecting/${provider}/schema/${objectName}/field-map`,
        { decisions },
      ),
  },
  sync: {
    status: (provider: Prospecting.Provider, objectName: string) =>
      IntegrationsClient.get<Prospecting.SyncStatusResponse>(`/prospecting/${provider}/sync`, {
        objectName,
      }),
    request: (provider: Prospecting.Provider, objectName: string) =>
      IntegrationsClient.post<{ started: boolean; run: Prospecting.SyncRun }>(
        `/prospecting/${provider}/sync?objectName=${objectName}`,
      ),
    settings: (provider: Prospecting.Provider, objectName: string) =>
      IntegrationsClient.get<Prospecting.SyncSettings>(`/prospecting/${provider}/sync/settings`, {
        objectName,
      }),
    saveSettings: (
      provider: Prospecting.Provider,
      settings: { objectName: string; refreshIntervalHours: number; autoRefreshEnabled: boolean },
    ) => IntegrationsClient.put(`/prospecting/${provider}/sync/settings`, settings),
  },
  duplicates: {
    /** Spans every CRM the advisor has connected, so it is not scoped to a provider. */
    list: () => IntegrationsClient.get<Prospecting.DuplicatesResponse>('/prospecting/duplicates'),
    resolve: (decision: {
      matchKey: string;
      decision: Prospecting.DuplicateStatus;
      keptProspectId?: string | null;
    }) => IntegrationsClient.put('/prospecting/duplicates', decision),
  },
  prospects: {
    /** Deletes one person's cached data. Retention is indefinite, so this is the removal path. */
    deleteByEmail: (provider: Prospecting.Provider, email: string) =>
      IntegrationsClient.delete<{ deleted: number }>(`/prospecting/${provider}/prospects`, {
        email,
      }),
    /** The scored, ranked book, one page at a time, with the book's status. */
    list: (provider: Prospecting.Provider, query: Prospecting.ProspectListQuery) =>
      IntegrationsClient.get<Prospecting.ProspectListResponse>(`/prospecting/${provider}/prospects`, query),
    detail: (provider: Prospecting.Provider, prospectId: string) =>
      IntegrationsClient.get<Prospecting.ProspectDetail>(`/prospecting/${provider}/prospects/${prospectId}`),
    feedback: (
      provider: Prospecting.Provider,
      prospectId: string,
      feedback: { vote: Prospecting.FeedbackVote; reasonCode?: string | null },
    ) => IntegrationsClient.post(`/prospecting/${provider}/prospects/${prospectId}/feedback`, feedback),
  },
  consent: {
    /** Tells the integrations service this advisor's CRM consent changed, so it re-reads it now. */
    changed: () => IntegrationsClient.post<{ cleared: boolean }>('/prospecting/consent-changed'),
  },
  scoring: {
    settings: (provider: Prospecting.Provider) =>
      IntegrationsClient.get<Prospecting.ScoringSettingsResponse>(`/prospecting/${provider}/scoring/settings`),
    saveSettings: (provider: Prospecting.Provider, settings: Prospecting.ScoringSettingsUpdate) =>
      IntegrationsClient.put<Prospecting.ScoringSettingsResponse>(`/prospecting/${provider}/scoring/settings`, settings),
  },
};
